import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, AppSettingsContext } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Check, Crown, CreditCard, Zap, Megaphone, ArrowRight, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Start with loading true
  const [currentPlan, setCurrentPlan] = useState('free');
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [paymentSettings, setPaymentSettings] = useState({ stripe: null, paypal: null });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: 'Perfect for getting started',
      features: [
        'Track up to 10 bets/month',
        'Ads Enabled',
        'Basic statistics',
        'Manual bet entry',
        'Basic filtering',
        'AI Predictions (daily picks)'
      ],
      current: false
    },
    {
      id: 'premium',
      name: 'Premium',
      monthlyPrice: 9.99,
      yearlyPrice: 99.99, // 16% discount (~2 months free)
      description: 'For serious bettors',
      features: [
        'Unlimited bets',
        'No Ads',
        'Advanced analytics',
        'Detailed reports by league & tipster',
        'Export data',
        'Priority support',
        'Advanced filtering',
        'AI Predictions with statistics',
        'AI Prediction performance history'
      ],
      current: false,
      popular: true
    },
    {
      id: 'tipster',
      name: 'Tipster',
      monthlyPrice: 29.99,
      yearlyPrice: 299.99, // 16% discount (~2 months free)
      description: 'Grow your tipster business',
      features: [
        'All Premium features',
        'List your tipster service',
        'Classified ad on Tip Services page',
        'Dedicated service page',
        'Share social networks & contact',
        'Earn money from selling tips',
        'Reach thousands of users',
        'Build your brand'
      ],
      current: false,
      featured: true
    }
  ]);

  useEffect(() => {
    // Always fetch plans (for public page)
    fetchPlans();
    fetchPaymentSettings();
    
    if (user) {
      setCurrentPlan(user.subscription_plan);
    }
  }, [user]);
  
  // Handle return from payment (Stripe/PayPal redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus || sessionId) {
      // Clear URL parameters to prevent issues on back button
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (paymentStatus === 'success') {
        toast.success('Payment successful! Your subscription has been activated.');
      } else if (paymentStatus === 'cancelled') {
        toast.info('Payment cancelled. You can try again anytime.');
      }
    }
  }, []);

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings-public`);
      setPaymentSettings(response.data);
      
      // Set default payment method based on what's enabled
      if (response.data.stripe?.enabled) {
        setSelectedPaymentMethod('stripe');
      } else if (response.data.paypal?.enabled) {
        setSelectedPaymentMethod('paypal');
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription-plans`);
      
      const backendPlans = response.data.plans;
      
      setPlans(prev => prev.map(plan => {
        const backendPlan = backendPlans[plan.id];
        
        // Update prices from backend
        const updatedPlan = {
          ...plan,
          monthlyPrice: backendPlan.price,
          monthlyDiscountPrice: backendPlan.discount_price || null,
          yearlyPrice: backendPlan.yearly_price || backendPlan.price * 12,
          discountPercentage: backendPlan.discount_percentage || 0,
          yearlyDiscountPercentage: backendPlan.discount_percentage || 0, // Yearly discount
          current: plan.id === user?.subscription_plan,
          enabled: backendPlan.enabled !== false // Add enabled field from backend
        };
        
        return updatedPlan;
      }));
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Use default prices if fetch fails
      setPlans(prev => prev.map(plan => ({
        ...plan,
        current: plan.id === user?.subscription_plan
      })));
    } finally {
      setLoading(false); // Set loading to false after plans are fetched
    }
  };

  const handleUpgrade = async (planId) => {
    // Redirect to login if user is not logged in
    if (!user) {
      toast.info('Please sign in to upgrade your plan');
      navigate('/login', { state: { from: '/subscription' } });
      return;
    }
    // Open payment method selection modal
    setSelectedPlan(planId);
    setShowPaymentModal(true);
  };

  const confirmPayment = () => {
    setShowPaymentModal(false);
    if (selectedPaymentMethod === 'stripe') {
      handleStripeCheckout(selectedPlan);
    } else if (selectedPaymentMethod === 'paypal') {
      handlePayPalCheckout(selectedPlan);
    }
  };

  const handleStripeCheckout = async (planId) => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    const originUrl = window.location.origin;

    try {
      const response = await axios.post(
        `${API}/payments/stripe/checkout?package_id=${planId}&origin_url=${encodeURIComponent(originUrl)}&billing_period=${billingPeriod}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start Stripe checkout');
      setLoading(false);
    }
  };

  const handlePayPalCheckout = async (planId) => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    const originUrl = window.location.origin;

    try {
      const response = await axios.post(
        `${API}/payments/paypal/checkout?package_id=${planId}&origin_url=${encodeURIComponent(originUrl)}&billing_period=${billingPeriod}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to PayPal checkout
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('Error creating PayPal checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start PayPal checkout');
      setLoading(false);
    }
  };

  // Plans are now managed through state - removed duplicate constant array

  // Guest header component for non-logged-in users (same as Landing page)
  const GuestHeader = () => (
    <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            {appSettings?.app_logo_url ? (
              <img 
                src={appSettings.app_logo_url} 
                alt={appSettings?.app_name || 'OddsChamp'} 
                className="h-10 max-w-[200px] object-contain"
              />
            ) : (
              <>
                <TrendingUp className="w-8 h-8 text-blue-400" />
                <span className="text-lg sm:text-xl font-bold text-white whitespace-nowrap">{appSettings?.app_name || 'OddsChamp'}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-gray-300 hover:text-white transition-colors font-medium">
              Blog
            </Link>
            <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full">
              Sign In
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );

  // Wrapper component based on user state
  const PageWrapper = ({ children }) => {
    if (user) {
      return <Layout>{children}</Layout>;
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <GuestHeader />
        <div className="pt-16">
          {children}
        </div>
        <Footer />
      </div>
    );
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white">Loading subscription plans...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div data-testid="subscription-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Plans & Features</h1>
            <p className="text-gray-300 text-lg">Select the plan that fits your betting tracking needs</p>
          </div>

          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white/5 border border-white/10 rounded-lg p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'yearly'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Yearly
                {(() => {
                  const premiumPlan = plans.find(p => p.id === 'premium');
                  return premiumPlan?.yearlyDiscountPercentage > 0 && (
                    <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                      Save {premiumPlan.yearlyDiscountPercentage}%
                    </span>
                  );
                })()}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const isDisabled = plan.enabled === false;
              
              return (
              <Card
                key={plan.id}
                data-testid={`plan-${plan.id}`}
                className={`relative ${
                  plan.popular
                    ? 'glass border-blue-500/50 shadow-2xl shadow-blue-500/20'
                    : 'glass-dark border-white/10'
                } card-hover ${isDisabled ? 'opacity-40' : ''}`}
              >
                {/* Disabled Overlay */}
                {isDisabled && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-lg z-10 flex items-center justify-center">
                    <div className="text-center p-6">
                      <div className="text-4xl mb-3">🔒</div>
                      <div className="text-xl font-bold text-white mb-2">Coming Soon</div>
                      <div className="text-sm text-gray-300">Stay tuned for updates</div>
                    </div>
                  </div>
                )}
                
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <Crown className="w-4 h-4" />
                      Most Popular
                    </span>
                  </div>
                )}
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      ⭐ Featured
                    </span>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-white text-2xl mb-2">{plan.name}</CardTitle>
                  <div className="mb-4">
                    {plan.id === 'free' ? (
                      <div>
                        <span className="text-4xl font-bold text-white">
                          <Price amount={0} />
                        </span>
                      </div>
                    ) : (
                      <div>
                        {/* MONTHLY BILLING */}
                        {billingPeriod === 'monthly' && (
                          <>
                            {/* Show discount for monthly if discount_price exists */}
                            {plan.monthlyDiscountPrice && plan.monthlyDiscountPrice < plan.monthlyPrice && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xl font-bold text-gray-500 line-through">
                                  <Price amount={plan.monthlyPrice} />
                                </span>
                                <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                                  {Math.round(((plan.monthlyPrice - plan.monthlyDiscountPrice) / plan.monthlyPrice) * 100)}% OFF
                                </span>
                              </div>
                            )}
                            
                            {/* Main monthly price (discounted if available) */}
                            <div className="flex items-baseline gap-1">
                              <span className="text-4xl font-bold text-white">
                                <Price amount={plan.monthlyDiscountPrice || plan.monthlyPrice} />
                              </span>
                              <span className="text-gray-400 text-lg">/month</span>
                            </div>
                            
                            {/* Show yearly savings info */}
                            {plan.yearlyDiscountPercentage > 0 && (
                              <p className="text-green-400 text-sm mt-1">
                                💡 Yearly: <Price amount={plan.yearlyPrice} /> (Save {plan.yearlyDiscountPercentage}%)
                              </p>
                            )}
                          </>
                        )}
                        
                        {/* YEARLY BILLING */}
                        {billingPeriod === 'yearly' && (
                          <>
                            {/* Show discount for yearly */}
                            {plan.yearlyDiscountPercentage > 0 && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xl font-bold text-gray-500 line-through">
                                  <Price amount={plan.monthlyPrice * 12} />
                                </span>
                                <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                                  {plan.yearlyDiscountPercentage}% OFF
                                </span>
                              </div>
                            )}
                            
                            {/* Main yearly price */}
                            <div className="flex items-baseline gap-1">
                              <span className="text-4xl font-bold text-white">
                                <Price amount={plan.yearlyPrice} />
                              </span>
                              <span className="text-gray-400 text-lg">/year</span>
                            </div>
                            
                            {/* Show monthly equivalent */}
                            <p className="text-green-400 text-sm mt-1">
                              <Price amount={plan.yearlyPrice / 12} />/month
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-400">{plan.description}</p>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <Check className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.current ? (
                    <Button
                      data-testid={`current-plan-${plan.id}`}
                      disabled
                      className="w-full bg-gray-600 text-white cursor-not-allowed"
                    >
                      Current Plan
                    </Button>
                  ) : plan.id === 'premium' || plan.id === 'tipster' ? (
                    <>
                      <Button
                        data-testid={`upgrade-button-${plan.id}`}
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white btn-hover"
                      >
                        {loading ? 'Processing...' : `Upgrade to ${plan.name}`}
                      </Button>
                      
                      {/* Learn More button for Tipster plan */}
                      {plan.id === 'tipster' && (
                        <Button
                          onClick={() => navigate('/become-tipster')}
                          variant="outline"
                          className="w-full mt-2 border-purple-500/50 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300"
                        >
                          📖 Learn More About Being a Tipster
                        </Button>
                      )}
                      
                      <p className="text-xs text-gray-400 text-center mt-3">
                        ✓ No recurring payments - One-time charge
                      </p>
                      {billingPeriod === 'monthly' && plan.discountPercentage > 0 && (
                        <p className="text-xs text-green-400 text-center mt-1">
                          💡 Switch to yearly and save {plan.discountPercentage}%!
                        </p>
                      )}
                    </>
                  ) : (
                    <Button
                      disabled
                      className="w-full bg-gray-600 text-white cursor-not-allowed"
                    >
                      Downgrade Not Available
                    </Button>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>

          {/* FAQ Section */}
          <Card className="glass-dark border-white/10 mt-12">
            <CardHeader>
              <CardTitle className="text-white">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h4 className="font-semibold text-white mb-1">Can I cancel anytime?</h4>
                  <p className="text-sm">Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">What payment methods do you accept?</h4>
                  <p className="text-sm">We accept all major credit cards through Stripe and PayPal.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Can I upgrade or downgrade my plan?</h4>
                  <p className="text-sm">You can upgrade to Premium at any time. Contact support for downgrades.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advertising Promotion Box */}
          <Card className="glass-dark border-white/10 mt-8 bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Megaphone className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Reach Thousands of Active Bettors
                    </h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Are you interested in advertising your betting products or services? 
                    Showcase your brand across our high-traffic platform with premium ad placements 
                    on Dashboard, Tips, and Strategies pages. Connect with engaged users ready to discover new betting solutions.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    onClick={() => navigate('/advertising')}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-6 text-base font-semibold shadow-lg hover:shadow-blue-500/50 transition-all"
                  >
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment Method Selection Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="glass-dark border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white text-center flex items-center justify-center text-xl">
                <CreditCard className="w-6 h-6 mr-2 text-blue-400" />
                Choose Payment Method
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-gray-300 text-center text-sm mb-6">
                Select how you'd like to pay for your subscription
              </p>

              <div className="space-y-3">
                {paymentSettings.stripe?.enabled && (
                  <button
                    onClick={() => setSelectedPaymentMethod('stripe')}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedPaymentMethod === 'stripe'
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-white text-lg">Stripe</div>
                        <div className="text-xs text-gray-400 mt-1">Pay with Credit/Debit Card</div>
                      </div>
                      {selectedPaymentMethod === 'stripe' && (
                        <Check className="w-6 h-6 text-blue-400" />
                      )}
                    </div>
                  </button>
                )}

                {paymentSettings.paypal?.enabled && (
                  <button
                    onClick={() => setSelectedPaymentMethod('paypal')}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedPaymentMethod === 'paypal'
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-white text-lg">PayPal</div>
                        <div className="text-xs text-gray-400 mt-1">Pay with PayPal Account or Card</div>
                      </div>
                      {selectedPaymentMethod === 'paypal' && (
                        <Check className="w-6 h-6 text-blue-400" />
                      )}
                    </div>
                  </button>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowPaymentModal(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmPayment}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Processing...' : 'Continue to Payment'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageWrapper>
  );
};

export default Subscription;
